import { IAgent } from '../types/agent';
import { getFirstLetter } from '../utils';
import { formatDate } from '../utils';
import { ArrowUpRight, Minus, Plus } from 'lucide-react';

const formattedData = (agent: any) => {
  return {
    name: agent.name || agent.personalInfo?.firstName,
    profilePic: (() => {
      const pic = agent.profileUri || agent.personalInfo?.profilePic;
      return pic?.startsWith('http') ? pic : undefined;
    })(),
    email: agent.personalEmail || agent?.email,
    dob: formatDate(agent.dob || agent.personalInfo?.dob),
    totalExperience:
      agent?.totalExperience || agent?.metadata?.totalWorkExperience,
    employers:
      (agent.pastEmployers || agent.workExperience?.pastEmployers)
        ?.map((e: any) => e.employerName || e.companyName)
        ?.join(', ') || '--',
    languages:
      (agent?.languages || agent?.questionnaire?.languages || [])
        ?.map((l: any) => l.language)
        ?.join(', ') || '--',
    tools:
      (agent?.tools || agent?.metadata?.toolsWorkedOn || [])?.join(', ') ||
      '--',
    industry: agent?.industry || '--',
    relevantIndustries: (agent?.relevantIndustries || [])?.join(', ') || '--',
    channels:
      (agent?.channels || agent?.metadata?.channels || [])
        ?.map((c: string) => c)
        ?.filter(Boolean)
        ?.join(', ') || '--',

    address: agent?.currentAddress || agent?.contactInfo?.address?.country,
  };
};

export const AgentCard = ({
  agent,
  onClick,
  isSelected,
  onToggle,
}: {
  agent: IAgent;
  onClick: () => void;
  isSelected?: boolean;
  onToggle?: () => void;
}) => {
  const info = formattedData(agent);

  return (
    <div
      onClick={onClick}
      className="relative group p-4 bg-white border border-gray-200 rounded-lg shadow hover:shadow-md transition cursor-pointer"
    >
      {onToggle && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // prevent card click
            onToggle();
          }}
          className="absolute top-2 right-2 z-10 bg-white border border-gray-300 rounded-full w-6 h-6 flex items-center justify-center text-xl font-bold text-gray-700 hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition"
          title={isSelected ? 'Remove Agent' : 'Add Agent'}
        >
          {isSelected ? <Minus size={16} /> : <Plus size={16} />}
        </button>
      )}

      <div className="flex items-center gap-3">
        {info.profilePic ? (
          <img
            loading="lazy"
            src={info.profilePic}
            alt={agent.name}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div
            className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold"
            aria-label={`Profile placeholder for ${agent.name}`}
          >
            {getFirstLetter(info.name)}
          </div>
        )}
        <div>
          <h4 className="text-md font-semibold">{info.name}</h4>
          <p className="text-sm text-gray-600">{info.email}</p>
        </div>
      </div>

      <div className="mt-2 text-sm text-gray-700">
        <p className="flex items-center justify-between">
          <strong>Industry:</strong> {info.industry}
        </p>
        <p className="flex items-center justify-between">
          <strong>Channels:</strong> {info.channels}
        </p>
        <p className="flex items-center justify-between truncate">
          <strong>Tools:</strong> {info.tools}
        </p>
        <p className="flex items-center justify-between">
          <strong>Languages:</strong> {info.languages}
        </p>
      </div>
    </div>
  );
};

export const AgentDetailCard = ({ agent }: { agent: any }) => {
  const info = formattedData(agent);

  return (
    <div className="space-y-6">
      {/* Profile */}
      <div className="flex items-center gap-5">
        {info.profilePic ? (
          <img
            src={info.profilePic}
            alt={info.name}
            className="w-20 h-20 rounded-full object-cover border"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-xl">
            {info.name?.[0] || 'A'}
          </div>
        )}
        <div>
          <h2 className="text-xl font-semibold">{info.name || 'Agent'}</h2>
          <p className="text-greyNinety font-regular text-sm">
            {info.email || 'No email provided'}
          </p>
          <p
            onClick={() => {
              window.open(`/agents/${agent._id}`, '_blank');
            }}
            className="flex items-center gap-1 text-blue-600 mt-1 font-normal text-xs cursor-pointer"
          >
            View full profile <ArrowUpRight size={14} />
          </p>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm text-gray-700">
        <Detail label="Date of Birth" value={info.dob} />

        <Detail label="Current Address" value={info.address || '--'} />
        <Detail
          label="Total Experience"
          value={`${info.totalExperience || '--'} yrs`}
        />
        <Detail label="Employers" value={info.employers} />
        <Detail label="Languages" value={info.languages} />
        <Detail label="Tools Worked On" value={info.tools} />
        <Detail label="Channels" value={info.channels} />
        <Detail label="Industry" value={info.industry} />
        <Detail label="Relevant Industries" value={info.relevantIndustries} />
      </div>
    </div>
  );
};

export const Detail = ({ label, value }: { label: string; value?: string }) => (
  <div>
    <p className="text-blackTen text-xs font-semibold uppercase">{label}</p>
    <p className="text-gray-800">{value || '--'}</p>
  </div>
);
